/* eslint-disable */
// src/pages/SmrutiStorage.jsx — Warm Light Theme + Public Feed option
import React, { useState, useRef } from 'react';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { ref as dbRef, push, set, get } from 'firebase/database';
import { db, storage } from '../environments/firebase';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Theme (matches Pravachan.tsx) ───────────────────────────
const T = {
  bg:       '#F7F5F0',
  surface:  '#FFFFFF',
  surface2: '#F0EDE8',
  border:   '#E5E0D8',
  text:     '#1A1714',
  muted:    '#8A8178',
  accent:   '#C2610A',
  accentBg: '#FEF3E8',
  green:    '#15803D',
  blue:     '#2563EB',
  red:      '#DC2626',
  shadow:   '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)',
  shadowMd: '0 4px 12px rgba(0,0,0,0.08)',
};

// ─── Icons ────────────────────────────────────────────────────
const Ic = {
  Back:  () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>,
  Check: () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>,
  Photo: () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  Audio: () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>,
  Video: () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>,
  Close: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Globe: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
  Lock:  () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>,
};

export default function SmrutiStorage() {
  const { currentUser } = useAuth();
  const [mode, setMode] = useState('pick'); // 'pick' | 'compose'
  const [title, setTitle]           = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate]             = useState('');
  const [isDateKnown, setIsDateKnown] = useState('yes');
  const [withWhom, setWithWhom]     = useState('');
  const [mediaType, setMediaType]   = useState('');
  const [mediaFile, setMediaFile]   = useState(null);
  const [postPublicly, setPostPublicly] = useState(true); // default PUBLIC

  const [isUploading, setIsUploading]       = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadTask, setUploadTask]         = useState(null);
  const [success, setSuccess]               = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setMediaFile(file);
    setMediaType(file.type.split('/')[0] || '');
  };

  const triggerFileInput = (type) => {
    setMediaType(type);
    fileInputRef.current && fileInputRef.current.click();
  };

  const cancelUpload = () => {
    if (uploadTask) { uploadTask.cancel(); }
    setIsUploading(false);
    setUploadProgress(0);
    setUploadTask(null);
  };

  const getDisplayInfo = async () => {
    if (!currentUser) return { displayName: 'Anonymous' };
    try {
      const snap = await get(dbRef(db, `users/${currentUser.uid}/profile`));
      if (snap.exists()) {
        const p = snap.val();
        return {
          displayName: (((p.firstName || '') + ' ' + (p.lastName || '')).trim()) || currentUser.email.split('@')[0] || 'Devotee',
          avatarUrl: p.avatarUrl || null,
        };
      }
    } catch (_) {}
    return { displayName: (currentUser.email && currentUser.email.split('@')[0]) || 'Devotee' };
  };

  const handleSubmit = async () => {
    if (!currentUser || !title.trim() || !description.trim()) return;
    if (isDateKnown === 'yes' && !date) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      let mediaUrl = '';
      if (mediaFile) {
        const sRef = storageRef(storage, `smruti/${currentUser.uid}/${Date.now()}_${mediaFile.name}`);
        const task = uploadBytesResumable(sRef, mediaFile);
        setUploadTask(task);
        await new Promise((resolve, reject) => {
          task.on(
            'state_changed',
            (snap) => setUploadProgress((snap.bytesTransferred / snap.totalBytes) * 100),
            reject,
            async () => { mediaUrl = await getDownloadURL(task.snapshot.ref); resolve(); }
          );
        });
        setUploadTask(null);
      }

      const ts = Date.now();
      const smrutiRef = dbRef(db, `users/${currentUser.uid}/smruti`);
      const newRef = push(smrutiRef);
      const smrutiData = {
        title:       title.trim(),
        description: description.trim(),
        date:        isDateKnown === 'yes' ? date : 'unknown',
        withWhom:    withWhom.trim(),
        mediaType,
        mediaUrl,
        isPublic:    postPublicly,
        createdAt:   ts,
      };
      await set(newRef, smrutiData);

      // Post to public feed if toggled on
      if (postPublicly) {
        const { displayName, avatarUrl } = await getDisplayInfo();
        const feedId = `smruti_${currentUser.uid}_${ts}`;
        await set(dbRef(db, `publicFeed/${feedId}`), {
          type:          'smruti',
          userId:        currentUser.uid,
          displayName,
          avatarInitial: displayName.charAt(0).toUpperCase(),
          avatarUrl:     avatarUrl || null,
          title:         title.trim(),
          description:   description.trim(),
          date:          isDateKnown === 'yes' ? date : 'unknown',
          withWhom:      withWhom.trim(),
          mediaType,
          mediaUrl,
          // Feed compatibility fields
          topic:         title.trim(),
          vakta:         withWhom.trim() ? ('With ' + withWhom.trim()) : '',
          caption:       description.trim(),
          dateTime:      new Date(ts).toISOString(),
          notes:         '',
          images:        mediaType === 'image' && mediaUrl ? [mediaUrl] : [],
          audioFiles:    mediaType === 'audio' && mediaUrl ? [mediaUrl] : [],
          createdAt:     ts,
          likes: {}, comments: {}, saves: {},
        });
      }

      // Reset
      setTitle('');
      setDescription('');
      setDate('');
      setIsDateKnown('yes');
      setWithWhom('');
      setMediaType('');
      setMediaFile(null);
      setUploadProgress(0);
      setPostPublicly(true);
      setSuccess(true);
      setMode('pick');
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      if (!err || err.code !== 'storage/canceled') {
        alert('Error saving smruti. Please try again.');
      }
    } finally {
      setIsUploading(false);
      setUploadTask(null);
    }
  };

  const canSubmit = title.trim() && description.trim() && (isDateKnown === 'no' || date) && !isUploading;

  const mediaOptions = [
    { type: 'image', label: 'Photo', icon: <Ic.Photo />, grad: 'linear-gradient(135deg,#D97706,#92400E)' },
    { type: 'audio', label: 'Audio', icon: <Ic.Audio />, grad: 'linear-gradient(135deg,#7C3AED,#4C1D95)' },
    { type: 'video', label: 'Video', icon: <Ic.Video />, grad: 'linear-gradient(135deg,#059669,#065F46)' },
  ];

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'transparent', border: 'none', outline: 'none',
    color: T.text, fontFamily: 'inherit', fontSize: 15,
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "-apple-system,'Helvetica Neue',sans-serif", color: T.text, maxWidth: 480, margin: '0 auto' }}>

      {/* Success overlay */}
      <AnimatePresence>
        {success && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(247,245,240,0.96)', backdropFilter: 'blur(8px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              style={{ width: 72, height: 72, borderRadius: '50%', background: T.green, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <Ic.Check />
            </motion.div>
            <p style={{ fontSize: 18, fontWeight: 700, color: T.text }}>
              {postPublicly ? 'Smruti shared!' : 'Smruti saved privately!'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">

        {/* ── STEP 1: Pick ── */}
        {mode === 'pick' && (
          <motion.div key="pick" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            style={{ padding: '24px 20px' }}>

            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 24, fontWeight: 700, margin: '0 0 6px', color: T.text }}>📿 Smruti</h1>
              <p style={{ color: T.muted, margin: 0, fontSize: 15 }}>Preserve your spiritual memories and divine experiences</p>
            </div>

            {/* Default public notice */}
            <div style={{ background: T.accentBg, border: '1px solid rgba(194,97,10,0.25)', borderRadius: 12, padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Ic.Globe />
              <p style={{ margin: 0, fontSize: 13, color: T.accent, fontWeight: 600 }}>Smrutis are shared to the community feed by default. You can keep them private in the next step.</p>
            </div>

            <button
              onClick={() => setMode('compose')}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16, background: T.surface, border: '1px solid ' + T.border, borderRadius: 16, padding: '18px 20px', cursor: 'pointer', textAlign: 'left', boxShadow: T.shadow }}
            >
              <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg,#7C3AED,#4C1D95)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0, fontSize: 22 }}>
                📿
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: T.text }}>New Smruti</p>
                <p style={{ margin: '3px 0 0', fontSize: 13, color: T.muted }}>Record a spiritual memory or experience</p>
              </div>
            </button>
          </motion.div>
        )}

        {/* ── STEP 2: Compose ── */}
        {mode === 'compose' && (
          <motion.div key="compose" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
            style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

            {/* Sticky top bar */}
            <div style={{ position: 'sticky', top: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'rgba(247,245,240,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid ' + T.border }}>
              <button onClick={() => setMode('pick')} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', padding: 4 }}>
                <Ic.Back />
              </button>
              <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: 'Georgia, serif' }}>New Smruti</span>
              <button onClick={handleSubmit} disabled={!canSubmit}
                style={{ background: canSubmit ? T.accent : T.border, color: canSubmit ? '#fff' : T.muted, border: 'none', borderRadius: 20, padding: '7px 18px', fontSize: 14, fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed', transition: 'all 0.15s', boxShadow: canSubmit ? '0 2px 8px rgba(194,97,10,0.3)' : 'none' }}>
                {isUploading ? (Math.round(uploadProgress) + '%') : (postPublicly ? 'Share' : 'Save')}
              </button>
            </div>

            <div style={{ padding: '16px 16px 0', flex: 1 }}>

              {/* Meta fields card */}
              <div style={{ background: T.surface, borderRadius: 14, border: '1px solid ' + T.border, marginBottom: 12, boxShadow: T.shadow }}>
                {/* Title */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', borderBottom: '1px solid ' + T.border }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.muted, width: 76, flexShrink: 0 }}>Title</span>
                  <input
                    style={inputStyle}
                    placeholder="Name this memory…"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    disabled={isUploading}
                  />
                </div>
                {/* Date known */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', borderBottom: '1px solid ' + T.border, gap: 16 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.muted, width: 76, flexShrink: 0 }}>Date?</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: T.text, cursor: 'pointer' }}>
                    <input type="radio" name="isDateKnown" value="yes" checked={isDateKnown === 'yes'} onChange={() => setIsDateKnown('yes')} style={{ accentColor: T.accent }} /> Yes
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: T.text, cursor: 'pointer' }}>
                    <input type="radio" name="isDateKnown" value="no" checked={isDateKnown === 'no'} onChange={() => setIsDateKnown('no')} style={{ accentColor: T.accent }} /> Not sure
                  </label>
                </div>
                {/* Date picker */}
                {isDateKnown === 'yes' && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', borderBottom: '1px solid ' + T.border }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.muted, width: 76, flexShrink: 0 }}>Date</span>
                    <input
                      type="date"
                      style={{ ...inputStyle, colorScheme: 'light' }}
                      value={date}
                      onChange={e => setDate(e.target.value)}
                      disabled={isUploading}
                    />
                  </div>
                )}
                {/* With whom */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '11px 14px' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.muted, width: 76, flexShrink: 0 }}>With</span>
                  <input
                    style={inputStyle}
                    placeholder="Bhagwan, Guru, family…"
                    value={withWhom}
                    onChange={e => setWithWhom(e.target.value)}
                    disabled={isUploading}
                  />
                </div>
              </div>

              {/* Description */}
              <div style={{ background: T.surface, borderRadius: 14, border: '1px solid ' + T.border, marginBottom: 12, boxShadow: T.shadow, overflow: 'hidden' }}>
                <div style={{ padding: '8px 14px', borderBottom: '1px solid ' + T.border, background: T.surface2 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Description</span>
                </div>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe this spiritual memory in detail…"
                  rows={5}
                  disabled={isUploading}
                  style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', outline: 'none', color: T.text, fontFamily: 'inherit', fontSize: 15, lineHeight: 1.65, padding: '14px 16px', resize: 'none' }}
                />
              </div>

              {/* Media */}
              <div style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.muted, marginBottom: 10 }}>Add Media (Optional)</p>
                <div style={{ display: 'flex', gap: 10 }}>
                  {mediaOptions.map(opt => (
                    <button key={opt.type}
                      onClick={() => triggerFileInput(opt.type)}
                      disabled={isUploading}
                      style={{
                        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '14px 8px',
                        background: mediaType === opt.type ? T.accentBg : T.surface,
                        border: '2px solid ' + (mediaType === opt.type ? T.accent : T.border),
                        borderRadius: 14, cursor: isUploading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s', opacity: isUploading ? 0.5 : 1,
                      }}
                    >
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: mediaType === opt.type ? opt.grad : T.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: mediaType === opt.type ? '#fff' : T.muted, transition: 'all 0.2s' }}>
                        {opt.icon}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: mediaType === opt.type ? T.accent : T.muted }}>{opt.label}</span>
                    </button>
                  ))}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*,audio/*,video/*,.m4a,.aac,.wav" onChange={handleFileChange} style={{ display: 'none' }} />
                {mediaFile && (
                  <div style={{ marginTop: 10, background: T.surface2, border: '1px solid ' + T.border, borderRadius: 10, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: T.muted }}>📎 {mediaFile.name}</span>
                    <button onClick={() => { setMediaFile(null); setMediaType(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted }}>
                      <Ic.Close />
                    </button>
                  </div>
                )}
              </div>

              {/* ── VISIBILITY TOGGLE (same pattern as Pravachan) ── */}
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>Visibility</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {/* Public */}
                  <button onClick={() => setPostPublicly(true)} style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 10px',
                    background: postPublicly ? T.accentBg : T.surface,
                    border: '2px solid ' + (postPublicly ? T.accent : T.border),
                    borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s',
                    boxShadow: postPublicly ? '0 2px 8px rgba(194,97,10,0.15)' : T.shadow,
                  }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: postPublicly ? T.accent : T.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: postPublicly ? '#fff' : T.muted, transition: 'all 0.2s' }}>
                      <Ic.Globe />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: postPublicly ? T.accent : T.muted }}>Public</span>
                    <span style={{ fontSize: 11, color: T.muted, textAlign: 'center' }}>Shared to community</span>
                  </button>
                  {/* Private */}
                  <button onClick={() => setPostPublicly(false)} style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 10px',
                    background: !postPublicly ? '#F0F4FF' : T.surface,
                    border: '2px solid ' + (!postPublicly ? '#2563EB' : T.border),
                    borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s',
                    boxShadow: !postPublicly ? '0 2px 8px rgba(37,99,235,0.1)' : T.shadow,
                  }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: !postPublicly ? '#2563EB' : T.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: !postPublicly ? '#fff' : T.muted, transition: 'all 0.2s' }}>
                      <Ic.Lock />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: !postPublicly ? '#2563EB' : T.muted }}>Private</span>
                    <span style={{ fontSize: 11, color: T.muted, textAlign: 'center' }}>Only visible to you</span>
                  </button>
                </div>

                {!postPublicly && (
                  <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                    style={{ marginTop: 10, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#1D4ED8' }}>💡 This smruti will only be saved to your library, not shared publicly.</span>
                  </motion.div>
                )}
              </div>

              {/* Upload progress */}
              {isUploading && (
                <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1D4ED8' }}>
                      {uploadProgress < 100 ? ('Uploading… ' + Math.round(uploadProgress) + '%') : 'Finalizing…'}
                    </span>
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <button onClick={cancelUpload} style={{ fontSize: 12, color: T.red, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                    )}
                  </div>
                  <div style={{ height: 4, background: '#DBEAFE', borderRadius: 2 }}>
                    <div style={{ height: '100%', background: '#2563EB', borderRadius: 2, width: (uploadProgress + '%'), transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        input::placeholder, textarea::placeholder { color: ${T.muted}; }
        * { box-sizing: border-box; }
      `}</style>
    </div>
  );
}